import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { KioskDevice } from '../types';
import { mockKioskSettings } from '../data/mock-kiosk-data';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export function KioskDeviceAddPage() {
  const navigate = useNavigate();
  const { settingsId } = useSearch({ from: '/_authenticated/kiosk/devices/add' });
  
  const [device, setDevice] = useState<KioskDevice>({
    id: `device-${Date.now()}`,
    name: '',
    description: '',
    location: '',
    model: '',
    serialNumber: '',
    ipAddress: '',
    macAddress: '',
    lastPing: Math.floor(Date.now() / 1000) - 300, // 5 minutes ago
    status: 'offline',
    settingsId: settingsId || '',
    lastUpdated: Math.floor(Date.now() / 1000),
    notes: '',
    orientation: 'landscape',
    screenSize: '1920x1080',
    osVersion: 'Chrome OS 108.0.5359.172',
    batteryLevel: 100,
    storageUsed: 45,
    tags: []
  });
  
  const [availableSettings] = useState(mockKioskSettings);
  
  const handleSave = () => {
    // In a real app, this would be an API call to save the device
    alert('Device registered successfully!');
    navigate({ to: '/kiosk/devices', search: { settingsId: undefined } });
  };
  
  return (
    <>
      <PageHeader
        title="Register Kiosk Device"
        description="Add a new kiosk device to the system."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/devices" search={{ settingsId: undefined }}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Cancel
              </Link>
            </Button>
            <Button size="sm" onClick={handleSave}>
              <Save className="mr-2 h-4 w-4" />
              Register Device
            </Button>
          </>
        }
      />
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Device Information</CardTitle>
            <CardDescription>
              Basic information about this kiosk device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Device Name</Label>
                <Input 
                  id="name" 
                  value={device.name} 
                  onChange={(e) => setDevice({...device, name: e.target.value})}
                  placeholder="Enter device name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input 
                  id="location" 
                  value={device.location} 
                  onChange={(e) => setDevice({...device, location: e.target.value})}
                  placeholder="Where is this device located?"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={device.description} 
                onChange={(e) => setDevice({...device, description: e.target.value})}
                placeholder="Enter a description for this device"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="settingsId">Settings Profile</Label>
              <Select 
                value={device.settingsId}
                onValueChange={(value) => setDevice({...device, settingsId: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select settings profile" />
                </SelectTrigger>
                <SelectContent>
                  {availableSettings.map(settings => (
                    <SelectItem key={settings.id} value={settings.id}>
                      {settings.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Hardware Details</CardTitle>
            <CardDescription>
              Technical specifications of the kiosk device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input 
                  id="model" 
                  value={device.model} 
                  onChange={(e) => setDevice({...device, model: e.target.value})}
                  placeholder="Device model"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="serialNumber">Serial Number</Label>
                <Input 
                  id="serialNumber" 
                  value={device.serialNumber} 
                  onChange={(e) => setDevice({...device, serialNumber: e.target.value})}
                  placeholder="Device serial number"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="screenSize">Screen Resolution</Label>
                <Input 
                  id="screenSize" 
                  value={device.screenSize} 
                  onChange={(e) => setDevice({...device, screenSize: e.target.value})}
                  placeholder="e.g. 1920x1080"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="orientation">Orientation</Label>
                <Select 
                  value={device.orientation}
                  onValueChange={(value) => setDevice({...device, orientation: value as 'landscape' | 'portrait'})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select orientation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">Landscape</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="osVersion">OS Version</Label>
                <Input 
                  id="osVersion" 
                  value={device.osVersion} 
                  onChange={(e) => setDevice({...device, osVersion: e.target.value})}
                  placeholder="Operating system version"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Network Information</CardTitle>
            <CardDescription>
              Network configuration for the kiosk device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ipAddress">IP Address</Label>
                <Input 
                  id="ipAddress" 
                  value={device.ipAddress} 
                  onChange={(e) => setDevice({...device, ipAddress: e.target.value})}
                  placeholder="e.g. 192.168.1.100"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="macAddress">MAC Address</Label>
                <Input 
                  id="macAddress" 
                  value={device.macAddress} 
                  onChange={(e) => setDevice({...device, macAddress: e.target.value})}
                  placeholder="e.g. 00:1A:2B:3C:4D:5E"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
            <CardDescription>
              Other details and notes about this device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea 
                id="notes" 
                value={device.notes} 
                onChange={(e) => setDevice({...device, notes: e.target.value})}
                placeholder="Enter any additional notes about this device"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input 
                id="tags" 
                value={device.tags.join(', ')} 
                onChange={(e) => setDevice({
                  ...device, 
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                })}
                placeholder="Enter tags separated by commas"
              />
              <p className="text-xs text-muted-foreground">
                Enter tags separated by commas (e.g. lobby, touchscreen, public)
              </p>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-end">
          <Button size="lg" onClick={handleSave}>
            <Save className="mr-2 h-4 w-4" />
            Register Device
          </Button>
        </div>
      </div>
    </>
  );
}

export default KioskDeviceAddPage;
